import React from 'react';
import { Box, Typography, Avatar, Skeleton, Card, Tooltip, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { tokens, outcomeLabel } from '../theme';

export const pct = (n, digits = 0) => `${(n * 100).toFixed(digits)}%`;

export const formatTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';

export const formatDay = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : '';

// Probabilities from stored odds, for predictions saved before `probabilities` existed
export function probabilitiesOf(p) {
  if (p.probabilities) return p.probabilities;
  const h = 1 / p.home_odds, d = 1 / p.draw_odds, a = 1 / p.away_odds;
  const t = h + d + a;
  return { home: h / t, draw: d / t, away: a / t };
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      justifyContent="space-between"
      gap={2}
      mb={{ xs: 2.5, md: 3.5 }}
    >
      <Box minWidth={0}>
        {eyebrow && <Typography variant="overline" color="primary">{eyebrow}</Typography>}
        <Typography variant="h4" component="h1">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" mt={0.5} maxWidth={620}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && <Box display="flex" gap={1} flexWrap="wrap" flexShrink={0}>{actions}</Box>}
    </Box>
  );
}

export function StatTile({ label, value, hint, tone }) {
  return (
    <Card sx={{ p: { xs: 1.75, sm: 2.25 }, height: '100%' }}>
      <Typography variant="overline" color="text.secondary" display="block" noWrap>{label}</Typography>
      <Typography
        sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.15, color: tone || 'text.primary' }}
      >
        {value}
      </Typography>
      {hint && <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{hint}</Typography>}
    </Card>
  );
}

export function TeamCrest({ src, name, size = 28 }) {
  return (
    <Avatar
      src={src || undefined}
      alt=""
      variant="rounded"
      sx={{ width: size, height: size, bgcolor: tokens.surfaceRaised, fontSize: size * 0.42, fontWeight: 700, color: 'text.secondary', '& img': { objectFit: 'contain' } }}
    >
      {name?.[0]}
    </Avatar>
  );
}

// One team line inside a match card: crest, name, optional score
export function TeamLine({ name, logo, score, highlight, dim }) {
  return (
    <Box display="flex" alignItems="center" gap={1.25} minWidth={0}>
      <TeamCrest src={logo} name={name} />
      <Typography
        variant="body1"
        noWrap
        title={name}
        sx={{ flex: 1, fontWeight: highlight ? 700 : 500, color: dim ? 'text.secondary' : 'text.primary' }}
      >
        {name}
      </Typography>
      {score !== undefined && score !== null && (
        <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.15rem', minWidth: 20, textAlign: 'right' }}>
          {score}
        </Typography>
      )}
    </Box>
  );
}

// Stacked H/D/A probability bar with a 2px gap between segments and direct labels
export function ProbabilityBar({ probabilities, pick, height = 8, showLabels = true }) {
  const parts = ['home', 'draw', 'away'];
  return (
    <Box>
      <Box display="flex" gap="2px" height={height} borderRadius={height} overflow="hidden" role="img"
        aria-label={parts.map(k => `${outcomeLabel[k]} ${pct(probabilities[k])}`).join(', ')}>
        {parts.map(k => (
          <Tooltip key={k} title={`${outcomeLabel[k]} ${pct(probabilities[k], 1)}`} arrow>
            <Box sx={{
              width: `${probabilities[k] * 100}%`,
              bgcolor: tokens.outcome[k],
              opacity: !pick || pick === k ? 1 : 0.45,
              transition: 'opacity .2s',
            }} />
          </Tooltip>
        ))}
      </Box>
      {showLabels && (
        <Box display="flex" justifyContent="space-between" mt={0.75}>
          {parts.map(k => (
            <Typography key={k} variant="caption" sx={{ color: pick === k ? 'text.primary' : 'text.secondary', fontWeight: pick === k ? 700 : 500 }}>
              <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', bgcolor: tokens.outcome[k], mr: 0.5, verticalAlign: 'middle' }} />
              {outcomeLabel[k]} {pct(probabilities[k])}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function OutcomePill({ outcome, label }) {
  const color = tokens.outcome[outcome];
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: 1.5,
      fontSize: 12, fontWeight: 700, color: 'text.primary',
      bgcolor: alpha(color, 0.16), border: `1px solid ${alpha(color, 0.45)}`,
    }}>
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color }} />
      {label || outcomeLabel[outcome]}
    </Box>
  );
}

export function LiveDot() {
  return (
    <Box component="span" sx={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', bgcolor: tokens.status.live,
      boxShadow: `0 0 0 0 ${alpha(tokens.status.live, 0.6)}`,
      animation: 'livePulse 1.6s infinite',
      '@keyframes livePulse': {
        '0%': { boxShadow: `0 0 0 0 ${alpha(tokens.status.live, 0.6)}` },
        '70%': { boxShadow: `0 0 0 8px ${alpha(tokens.status.live, 0)}` },
        '100%': { boxShadow: `0 0 0 0 ${alpha(tokens.status.live, 0)}` },
      },
    }} />
  );
}

export function StatusBadge({ live }) {
  if (!live) return null;
  if (live.is_live) {
    return (
      <Box display="inline-flex" alignItems="center" gap={0.75} sx={{ color: tokens.status.live, fontSize: 12, fontWeight: 700 }}>
        <LiveDot /> LIVE {live.minute ? `· ${live.minute}` : ''}
      </Box>
    );
  }
  if (live.is_finished) {
    return <Typography variant="caption" fontWeight={700} color="text.secondary">FULL TIME</Typography>;
  }
  return <Typography variant="caption" fontWeight={700} color="text.secondary">{live.status?.toUpperCase()}</Typography>;
}

export function EmptyState({ icon, title, body, action }) {
  return (
    <Card sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center', borderStyle: 'dashed' }}>
      {icon && <Box sx={{ color: 'text.secondary', mb: 1.5, '& svg': { fontSize: 40 } }}>{icon}</Box>}
      <Typography variant="h6" gutterBottom>{title}</Typography>
      {body && <Typography variant="body2" color="text.secondary" maxWidth={420} mx="auto">{body}</Typography>}
      {action && <Box mt={2.5}>{action}</Box>}
    </Card>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <EmptyState
      title="Something went wrong"
      body={message}
      action={onRetry && <Button variant="outlined" onClick={onRetry}>Try again</Button>}
    />
  );
}

export function CardGridSkeleton({ count = 6, height = 190 }) {
  return (
    <Box display="grid" gap={2} gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}>
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} variant="rounded" height={height} sx={{ borderRadius: `${tokens.radius}px` }} />
      ))}
    </Box>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} mt={{ xs: 3, md: 4 }}>
      <Typography variant="h5" component="h2">{children}</Typography>
      {action}
    </Box>
  );
}

export const cardGridSx = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
};

// Pill-style segmented control for ToggleButtonGroup
export const segmentedSx = {
  p: 0.5,
  gap: 0.5,
  bgcolor: tokens.surfaceRaised,
  border: `1px solid ${tokens.border}`,
  borderRadius: 2.5,
  '& .MuiToggleButton-root': {
    border: 0,
    borderRadius: '8px !important',
    textTransform: 'none',
    fontWeight: 600,
    px: 1.5,
    py: 0.5,
    color: 'text.secondary',
    '&.Mui-selected': { bgcolor: alpha(tokens.accent, 0.18), color: 'text.primary' },
    '&.Mui-selected:hover': { bgcolor: alpha(tokens.accent, 0.24) },
  },
};
